import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth on mount
  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Try regular login first
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const data = response.data;

      if (data.mfa_required) {
        return { mfa_required: true, mfa_token: data.mfa_token, mfa_method: data.mfa_method };
      }

      const { access_token, user: userData, mfa_setup_required } = data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { ...userData, mfa_setup_required };
    } catch (err) {
      // If regular login fails, try team member login
      if (err.response?.status === 401) {
        try {
          const teamRes = await axios.post(`${API_URL}/team/login`, { email, password });
          const { access_token, user: userData } = teamRes.data;
          localStorage.setItem('token', access_token);
          setToken(access_token);
          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          return userData;
        } catch {
          // Both failed - throw original error
          throw err;
        }
      }
      throw err;
    }
  };

  const completeMfaLogin = async (mfaToken, code) => {
    const response = await axios.post(`${API_URL}/auth/mfa/verify`, { mfa_token: mfaToken, code });
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Set auth data directly (used by accept-invite flow)
  const setAuthData = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  };

  const value = {
    user,
    token,
    loading,
    login,
    completeMfaLogin,
    register,
    logout,
    setAuthData,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
